import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {ECMode, PlantIncentiveMode} from 'smag-commons/cfg/hierarchy';
import {
    ECCacvRedistributionMode,
    ECEconomicsPlantsResponse,
    EconomicsErrorType,
    ECPlantTip,
    ECPlantTipMode,
    ECPlantTipType,
    STRATEGY_OLD_CONDO
} from 'smag-commons/dto/eco';
import {I18NService} from '../../../../i18n/i18n.service';
import {DialogService} from '../../../../shared/dialog/dialog.service';
import {ValueObjectType} from 'smag-commons/cfg/value';
import {SmagApiService} from '../../../../api/smag-api.service';
import {MsgBoxService} from '../../../../shared/msg-box.service';
import {PlantsPod} from '../ec-plants.component';
import {isDefined} from '../../../../commons/commons';
import {Router} from '@angular/router';
import {HierarchyEntity} from '../../../../shared/hierarchy.service';
import {Mode} from '../../billing-filter/ec-billing-filter.component';
import {CONTENT_DIALOG, DATE_SELECTION} from '../../economics-aggregated/ec-select-date/ec-select-date.component';
import {ReiResponse} from '../set-rei/set-rei.component';
import {YearlyValue} from 'smag-commons/cfg/value/yearly-value';
import {EC_PLANTS_REI_REDISTRIBUTION, PLANT_TIP_QUOTE} from 'smag-commons/cfg/economics-values';
import {TipResponse} from '../set-tip/set-tip.component';
import {CACV_STRING, missingCosts, YearSummaryConsolidation} from '../../../../shared/ec-economics';
import {NiceValuePipe} from '../../../../shared/shared.pipe';
import {formatEuroValue, formatPercentValue} from '../../../../shared/ec-members';
import {EconomicsConfig} from 'smag-commons/cfg';


@Component({
    selector: 'app-ec-plants-details',
    templateUrl: './ec-plants-details.component.html',
    styleUrls: ['./ec-plants-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ECPlantsDetailsComponent implements OnInit {
    @Input() economicsPlantsResponse: ECEconomicsPlantsResponse;
    @Input() plantsPod: PlantsPod;
    @Input() entity: HierarchyEntity;
    @Input() date: Moment;
    @Input() timeZone: string;
    @Input() summaryConsolidations: YearSummaryConsolidation[];
    @Input() minDate: Moment;
    @Input() maxDate: Moment;
    @Input() isWholeYear: boolean;
    @Input() enableTipConfig: boolean;
    @Input() strategy: string;
    @Input() isSingleMonth: boolean;
    @Input() selectedYear: number;
    @Input() isRepresentative: boolean;
    @Input() generatesTip: boolean;
    @Input() economicsConfig: EconomicsConfig;
    @Output() detailsDataEvent: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('configureTipQuote') private configureTipQuoteDialog: TemplateRef<any>;
    @ViewChild('configureRei') private configureReiDialog: TemplateRef<any>;
    @ViewChild('selectDate') private selectDateDialog: TemplateRef<any>;
    @ViewChild('setBilledData') private setBilledDataDialog: TemplateRef<any>;

    currentDateSelection: DATE_SELECTION;
    currentContent: CONTENT_DIALOG;
    capitalGrantUse = false;

    Mode = Mode;
    ECMode = ECMode;
    EconomicsErrorType = EconomicsErrorType;
    readonly isDefined = isDefined;
    readonly missingCosts = missingCosts;
    readonly CACV_STRING = CACV_STRING;
    private readonly nvp: NiceValuePipe;
    readonly PlantIncentiveMode = PlantIncentiveMode;

    constructor(private readonly apiService: SmagApiService,
                private readonly msgBoxService: MsgBoxService,
                private readonly dialogService: DialogService,
                private readonly i18n: I18NService,
                private readonly router: Router) {
        this.currentContent = CONTENT_DIALOG.MONTHLY;
        this.currentDateSelection = DATE_SELECTION.MONTH;
        this.nvp = new NiceValuePipe(i18n);
    }

    ngOnInit() {
        this.capitalGrantUse = this.plantsPod.plants.some(plant => plant.capital_grant > 0);
    }

    get isCommunityPlant() {
        return !this.plantsPod.subject_id;
    }

    get isNotRepresentative() {
        return !this.isRepresentative || this.isRepresentative && this.isCommunityPlant;
    }

    get nonIncentivizedPlants() {
        return this.plantsPod.plants.length > 0 &&
            this.plantsPod.plants.some(plant => isDefined(plant.incentive_mode) &&
                plant.incentive_mode === PlantIncentiveMode.NO_INCENTIVE);
    }

    get plantTip() {
        if (!isDefined(this.plantsPod.tip_configs)) {
            return '-';
        }

        let tip: string;
        let tipQuote: string;
        if (this.plantsPod.tip_configs.type === ECPlantTipType.GROSS) {
            if (this.plantsPod.tip_configs.mode === ECPlantTipMode.PERCENTAGE) {
                tip = this.nvp.transform(this.plantsPod.gross_tip || 0, '€', 2, this.plantsPod.gross_tip < 0);
                tipQuote = this.nvp.transform((this.plantsPod.tip_configs.plant_tip || 0) * 100, '%', 2, this.plantsPod.tip_configs.plant_tip < 0);
            } else {
                tip = this.nvp.transform(this.plantsPod.incentive_shared_energy / 1000 || 0, 'MWh', 5, this.plantsPod.incentive_shared_energy < 0);
                tipQuote = this.nvp.transform((this.plantsPod.tip_configs.plant_tip || 0), '€/MWh', 2, this.plantsPod.tip_configs.plant_tip < 0);
            }
        } else if (this.plantsPod.tip_configs.type === ECPlantTipType.NET) {
            if (this.plantsPod.tip_configs.mode === ECPlantTipMode.PERCENTAGE) {
                tip = this.nvp.transform(this.plantsPod.net_tip || 0, '€', 2, this.plantsPod.net_tip < 0);
                tipQuote = this.nvp.transform((this.plantsPod.tip_configs.plant_tip || 0) * 100, '%', 2, this.plantsPod.tip_configs.plant_tip < 0);
            } else {
                tip = this.nvp.transform(this.plantsPod.incentive_shared_energy / 1000 || 0, 'MWh', 5, this.plantsPod.incentive_shared_energy < 0);
                tipQuote = this.nvp.transform((this.plantsPod.tip_configs.plant_tip || 0), '€/MWh', 2, this.plantsPod.tip_configs.plant_tip < 0);
            }
        } else {
            if (this.plantsPod.tip_configs.mode === ECPlantTipMode.PERCENTAGE) {
                tip = this.nvp.transform(this.plantsPod.non_surplus_tip || 0, '€', 2, this.plantsPod.non_surplus_tip < 0);
                tipQuote = this.nvp.transform((this.plantsPod.tip_configs.plant_tip || 0) * 100, '%', 2, this.plantsPod.tip_configs.plant_tip < 0);
            } else {
                tip = this.nvp.transform(this.plantsPod.incentivized_shared_energy_non_surplus / 1000 || 0, 'MWh', 5, this.plantsPod.incentivized_shared_energy_non_surplus < 0);
                tipQuote = this.nvp.transform((this.plantsPod.tip_configs.plant_tip || 0), '€/MWh', 2, this.plantsPod.tip_configs.plant_tip < 0);
            }
        }

        const plantTip = this.nvp.transform(this.plantsPod.plant_tip || 0, '€', 2, this.plantsPod.plant_tip < 0);
        return tip + ' * ' + tipQuote + ' = ' + plantTip;
    }

    get plantCacv() {
        if (!this.plantsPod || !isDefined(this.plantsPod.plant_c_acv)) {
            return '-';
        }
        if (this.strategy === STRATEGY_OLD_CONDO) {
            return formatEuroValue(this.plantsPod.plant_c_acv, this.nvp);
        }
        switch (this.plantsPod.cacvMode) {
            case ECCacvRedistributionMode.REDISTRIBUTION:
            case ECCacvRedistributionMode.PLANTS:
                return formatEuroValue(this.plantsPod.plant_c_acv, this.nvp);
            case ECCacvRedistributionMode.AS_TIP:
                return this.plantCacvAsTip();
            default:
                return this.nvp.transform(0, '€');
        }
    }

    get utilitiesAbsorptionFee() {
        const netTip = this.plantsPod.net_tip || 0;
        const grossTip = this.plantsPod.gross_tip || 0;
        const plantTip = this.plantsPod.plant_tip || 0;
        let tip: number;
        if (this.strategy === STRATEGY_OLD_CONDO) {
            tip = grossTip - plantTip;
        } else {
            tip = netTip - plantTip;
        }
        return this.i18n.translate('Utilities absorption fee') + ': ' + this.nvp.transform(tip, '€', 2, tip < 0);
    }

    get netTip() {
        return this.isDefined(this.plantsPod.net_tip) ?
            this.nvp.transform(this.plantsPod.net_tip, '€', 2, this.plantsPod.net_tip < 0) :
            '- €';
    }

    get grossTip() {
        return this.isDefined(this.plantsPod.gross_tip) ?
            this.nvp.transform(this.plantsPod.gross_tip, '€', 2, this.plantsPod.gross_tip < 0) :
            '- €';
    }

    openTipDialog() {
        this.dialogService.open<any, TipResponse>({
            title: this.i18n.translate('Configure the premium fee designated to "{0}"\'s plant', this.plantsPod.entity.name),
            template: this.configureTipQuoteDialog,
            context: {
                name: this.entity.name
            }
        }).closed.then(data => {
            if (data) {
                const value: YearlyValue = {
                    name: PLANT_TIP_QUOTE,
                    object_id: this.plantsPod.pod,
                    object_type: ValueObjectType.EC_POD,
                    value: {
                        type: data.type,
                        mode: data.mode,
                        plant_tip: data.plant_tip
                    } as ECPlantTip,
                    year: data.year
                };
                this.apiService.setYearlyValue(value).then(() => {
                        this.msgBoxService.info(this.i18n.translate('Plant configured correctly'));
                        this.detailsDataEvent.emit();
                    }
                ).catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while configuring plant'), err));
            }
        });
    }

    openReiDialog() {
        this.dialogService.open<any, ReiResponse>({
            title: this.i18n.translate('Configure the net revenue of the "{0}"\'s plants', this.plantsPod.entity.name),
            template: this.configureReiDialog,
            context: {
                name: this.entity.name
            }
        }).closed.then(data => {
            if (data) {
                const value: YearlyValue = {
                    name: EC_PLANTS_REI_REDISTRIBUTION,
                    object_id: this.plantsPod.pod,
                    object_type: ValueObjectType.EC_POD,
                    value: data.useReiVal ? 1 : 0,
                    year: data.year
                };
                this.apiService.setYearlyValue(value).then(() => {
                        this.msgBoxService.info(this.i18n.translate('Plant configured correctly'));
                        this.detailsDataEvent.emit();
                    }
                ).catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while configuring plant'), err));
            }
        });
    }

    openConfigureDialog() {
        if (this.isWholeYear) {
            if (!this.isCommunityPlant) {
                this.openTipDialog();
            } else {
                this.openReiDialog();
            }
        } else {
            this.currentContent = CONTENT_DIALOG.YEARLY;
            this.currentDateSelection = DATE_SELECTION.YEAR;
            this.dialogService.open<any, Moment>({
                title: this.i18n.translate('Select an annual period to configure'),
                template: this.selectDateDialog,
                context: {}
            }).closed.then(data => {
                if (data) {
                    this.selectedYear = data.year();
                    if (!this.isCommunityPlant) {
                        this.openTipDialog();
                    } else {
                        this.openReiDialog();
                    }
                }
            });
        }
    }

    onClickGoToPlant(id: string) {
        this.router.navigate(['/c', 'ec', 'resources'],
            {
                queryParams: {id},
                queryParamsHandling: 'merge'
            }).then();
    }

    hasFutureActivation(date: number) {
        return isDefined(date) ? date > moment.tz(this.timeZone).valueOf() : false;
    }

    openBilledDialog() {
        this.dialogService.open<any, any>({
            title: this.i18n.translate('Enter data billed on energy withdrawal input the plants of \"{0}\"', this.plantsPod.entity.name),
            template: this.setBilledDataDialog,
            context: {
                name: this.entity.name
            }
        }).closed.then(data => {
            // TODO send api req to insert billed data when BE is ready
        });
    }

    onClickInsertBilledData() {
        if (this.isSingleMonth) {
            this.openBilledDialog();
        } else {
            this.currentContent = CONTENT_DIALOG.MONTHLY;
            this.currentDateSelection = DATE_SELECTION.MONTH;
            this.dialogService.open<any, Moment>({
                title: this.i18n.translate('Select a month to insert billed data'),
                template: this.selectDateDialog,
                context: {}
            }).closed.then(data => {
                if (data) {
                    this.date = data;
                    this.openBilledDialog();
                }
            });
        }
    }

    onClickGoToMembers() {
        this.router.navigate(['/c', 'ec', 'members'],
            {
                queryParams: {id: this.entity.id},
                queryParamsHandling: 'merge'
            }).then();
    }

    private plantCacvAsTip(): string {
        if (!isDefined(this.plantsPod.tip_configs)) {
            return '-';
        }
        const tipMode = this.plantsPod.tip_configs.mode;
        const tipType = this.plantsPod.tip_configs.type;
        const plantCacv = formatEuroValue(this.plantsPod.plant_c_acv ?? 0, this.nvp);
        if (tipMode === ECPlantTipMode.PERCENTAGE) {
            const plantTip = formatPercentValue(this.plantsPod.tip_configs.plant_tip ?? 0, this.nvp);
            let cacvValue = 0;
            if (tipType === ECPlantTipType.GROSS) {
                cacvValue = this.plantsPod.gross_c_acv;
            } else if (tipType === ECPlantTipType.NET || tipType === ECPlantTipType.NON_SURPLUS) {
                cacvValue = this.plantsPod.net_c_acv;
            }
            const cacv = formatEuroValue(cacvValue ?? 0, this.nvp);
            return `${cacv} * ${plantTip} = ${plantCacv}`;
        } else if (tipMode === ECPlantTipMode.EURO_MGW) {
            const plantTip = formatEuroValue(this.plantsPod.plant_tip ?? 0, this.nvp);
            let tipValue = 0;
            let cacvValue = 0;
            if (tipType === ECPlantTipType.GROSS) {
                tipValue = this.plantsPod.gross_tip;
                cacvValue = this.plantsPod.gross_c_acv;
            } else if (tipType === ECPlantTipType.NET) {
                tipValue = this.plantsPod.net_tip;
                cacvValue = this.plantsPod.net_c_acv;
            } else if (tipType === ECPlantTipType.NON_SURPLUS) {
                tipValue = this.plantsPod.non_surplus_tip;
                cacvValue = this.plantsPod.net_c_acv;
            }
            const tip = formatEuroValue(tipValue ?? 0, this.nvp);
            const cacv = formatEuroValue(cacvValue ?? 0, this.nvp);
            return `(${plantTip} / ${tip}) * ${cacv} = ${plantCacv}`;
        }
    }

}
